// The links a menu block draws: the manager's own, plus the built-in
// Trails page, which every website has and which appears in the menu
// by itself — the same way the page exists without anybody making it.
//
// The Trails page's settings decide (Show in menu, Published); the
// server sends `site.trails_page` only when both say yes, and only for
// an organization with trails. Nothing is added when:
//   - the site has no public trails to list;
//   - the manager already links /trails themselves (theirs wins, in
//     their place and with their words);
//   - a link already carries the page's name and points somewhere real,
//     because two "Trails" in one menu is worse than either.
// A same-named link that only jumps to a spot on the page (#trails,
// the starter menu's) is pointed at the Trails page instead: on any
// page but the home page that anchor goes nowhere.

//
// And while the site has no public trail, the Trails page is not served
// to a visitor at all (SiteTrailsPage::hasPublicTrails), so a link the
// manager made to it is left out of the menu and the footer too — a
// link to a page that is not there is worse than none. The editor
// canvas keeps it, so the manager still sees what they set.

const norm = (href) => String(href || '').trim().replace(/[?#].*$/, '').replace(/\/+$/, '') || '/';
const isAnchorOnly = (href) => /^\/?#/.test(String(href || '').trim());

const TRAILS_HREF = '/trails';

/** The site has a public trail, so its Trails page has something to list. */
export function siteHasTrails(siteData) {
    return Array.isArray(siteData?.trail_groups) && siteData.trail_groups.some((g) => (g.trails || []).length);
}

/** Links a visitor can follow: none to the Trails page while it has nothing to list. */
export function shownLinks(links, siteData, editing = false) {
    const list = Array.isArray(links) ? links : [];
    if (editing || siteHasTrails(siteData)) return list;
    return list.filter((l) => !l || norm(l.href) !== TRAILS_HREF);
}

/** A footer's link columns as a visitor reads them; a column whose only
 *  links were to a page that is not served goes with them. */
export function footerColumns(columns, siteData, editing = false) {
    const all = Array.isArray(columns) ? columns : [];
    if (editing || siteHasTrails(siteData)) return all;
    return all
        .map((col) => (col && Array.isArray(col.links) ? { ...col, links: shownLinks(col.links, siteData) } : col))
        .filter((col, i) => !(col && Array.isArray(col.links) && col.links.length === 0 && (all[i].links || []).length > 0));
}

export function menuLinks(links, siteData, editing = false) {
    const list = shownLinks(links, siteData, editing).filter((l) => l && String(l.label ?? '').trim() !== '');
    const entry = siteData?.site?.trails_page;
    if (!entry || !siteHasTrails(siteData)) return list;
    if (list.some((l) => norm(l.href) === entry.href)) return list;

    const title = String(entry.title || 'Trails').trim();
    const named = list.findIndex((l) => String(l.label).trim().toLowerCase() === title.toLowerCase());
    if (named !== -1) {
        if (!isAnchorOnly(list[named].href)) return list;
        return list.map((l, i) => (i === named ? { ...l, href: entry.href } : l));
    }

    const link = { label: title, href: entry.href, builtin: true };
    const at = list.length && norm(list[0].href) === '/' ? 1 : 0;
    return [...list.slice(0, at), link, ...list.slice(at)];
}
